import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Home, PoundSterling, DollarSign, Euro, ChevronRight, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DealsListPage = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const statusRes = await fetch(`${API_URL}/api/public/deal-sourcing-enabled`);
        const status = statusRes.ok ? await statusRes.json() : { enabled: true };
        // Auto-redirect to partner site when configured
        if (status?.redirect_enabled && status?.redirect_url) {
          window.location.replace(status.redirect_url);
          return;
        }
        setEnabled(status?.enabled !== false);
        if (status?.enabled !== false) {
          const res = await fetch(`${API_URL}/api/public/deals`);
          if (res.ok) setDeals(await res.json());
        }
      } catch { }
      setLoading(false);
    };
    load();
  }, []);

  const filtered = deals.filter(d =>
    d.title?.toLowerCase().includes(search.toLowerCase()) ||
    d.location?.toLowerCase().includes(search.toLowerCase()) ||
    d.strategy_tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#0a1a14]">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <Link to="/" className="text-[#D4AF37] font-bold text-sm tracking-widest mb-2 block hover:opacity-80 transition-opacity">BLACK STAR PI</Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Investment Opportunities</h1>
            <p className="text-white/40 text-sm mt-1">Exclusive property deals sourced by our team</p>
          </div>
          <div className="w-full sm:w-auto">
            <div className="flex items-center bg-white/5 border border-white/10 rounded-lg overflow-hidden">
              <Search className="w-4 h-4 text-white/30 ml-3" />
              <input value={search} onChange={e => setSearch(e.target.value)} className="bg-transparent px-3 py-2.5 text-white text-sm focus:outline-none w-full sm:w-64" placeholder="Search deals..." data-testid="deals-search" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !enabled ? (
          <div className="text-center py-24" data-testid="deal-sourcing-disabled">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]/70">Temporarily Paused</span>
              <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]/50" />
            </div>
            <h3 className="text-white text-2xl md:text-3xl font-extralight tracking-tight mb-4">
              Deal Sourcing is <span className="italic text-[#D4AF37]">currently unavailable</span>
            </h3>
            <p className="text-white/50 text-sm max-w-lg mx-auto leading-relaxed">
              We&apos;re between releases. Register your interest and we&apos;ll notify you the moment new opportunities are live.
            </p>
            <Link
              to="/#contact"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#D4AF37]/10 transition-colors"
              data-testid="deal-sourcing-disabled-cta"
            >
              Get Notified
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <h3 className="text-white/40 text-lg mb-2">No deals found</h3>
            <p className="text-white/20 text-sm">Check back soon for new investment opportunities</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="deals-grid">
            {filtered.map(deal => (
              <Link key={deal.id} to={`/deals/${deal.slug}`}
                className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all group"
                data-testid={`deal-card-${deal.slug}`}
              >
                {/* Card Header */}
                <div className="bg-gradient-to-br from-[#D4AF37]/20 to-transparent p-5">
                  <h3 className="text-white font-bold text-base leading-tight group-hover:text-[#D4AF37] transition-colors line-clamp-3">{deal.title}</h3>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {deal.strategy_tags?.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-medium rounded-full">{tag}</span>
                    ))}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 space-y-3">
                  {deal.finders_fee > 0 && (() => {
                    const ccy = deal.finders_fee_currency || 'USD';
                    const Icon = ccy === 'GBP' ? PoundSterling : (ccy === 'EUR' ? Euro : DollarSign);
                    return (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
                        <span className="text-white/60">Finders Fee:</span>
                        <span className="text-white font-semibold">{ccy} {deal.finders_fee?.toLocaleString()}</span>
                      </div>
                    );
                  })()}
                  {deal.location && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-white/70">{deal.location}</span>
                    </div>
                  )}
                  {deal.property_type && (
                    <div className="flex items-center gap-2 text-sm">
                      <Home className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-white/70">{deal.property_type}</span>
                    </div>
                  )}
                </div>

                {/* Card Footer */}
                <div className="border-t border-white/5 px-5 py-3 flex items-center justify-between">
                  <span className="text-[#D4AF37] text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Details <ChevronRight className="w-3 h-3" />
                  </span>
                  {deal.deal_id && <span className="text-white/20 text-[10px]">ID: {deal.deal_id}</span>}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Bespoke Sourcing CTA — for investors who don't see what they're looking for */}
        {enabled && (<div className="mt-20 md:mt-28" data-testid="bespoke-cta-section">
          <div className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#0a1a14] via-[#0a1a14] to-[#0d231a]">
            {/* Decorative gold diagonal accent */}
            <div className="absolute top-0 right-0 w-64 h-64 pointer-events-none" aria-hidden>
              <div className="absolute inset-0 bg-gradient-to-bl from-[#D4AF37]/15 via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

            <div className="relative px-6 md:px-12 py-12 md:py-16 flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12">
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-4">Bespoke Deal Sourcing</p>
                <h3 className="text-2xl md:text-4xl font-extralight tracking-tight leading-tight mb-4 text-white">
                  Can't find what you're <span className="italic font-light text-[#D4AF37]">looking for?</span>
                </h3>
                <p className="text-white/55 text-sm md:text-base leading-relaxed max-w-xl">
                  The opportunities listed publicly are a fraction of what passes through our desk. Share your acquisition brief — our sourcing team will surface matches from our private pipeline within 48 hours.
                </p>
                <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
                  Confidential · Curated · Investor-Led
                </p>
              </div>
              <Link
                to="/bespoke-sourcing"
                className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8941E] text-[#0a1a14] text-xs font-bold uppercase tracking-[0.2em] shadow-lg shadow-[#D4AF37]/10 hover:shadow-[#D4AF37]/40 hover:-translate-y-0.5 transition-all whitespace-nowrap flex-shrink-0"
                data-testid="bespoke-sourcing-cta"
              >
                Apply for Bespoke Sourcing
                <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>)}
      </div>
    </div>
  );
};

export default DealsListPage;
